import "./inquiries.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../hooks/AuthProvider";
import API_BASE_URL from "../../../config/api";
import { Modal, Button, Spinner } from "react-bootstrap";
import { ReplyFill } from "react-bootstrap-icons";

const statusColors = { open: "statusOpen", replied: "statusReplied", closed: "statusClosed" };

function Inquiries() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [replyAlert, setReplyAlert] = useState(null);
  const { token } = useAuth();

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/v1/contact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContacts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load inquiries");
      console.error(err.message);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const handleToggleSeen = async (c) => {
    try {
      await axios.put(`${API_BASE_URL}/api/v1/contact/${c.id}`, { seen: !c.seen }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      fetchContacts();
    } catch (err) {
      console.error(err.message);
    }
  };

  const handleChangeStatus = async (id, status) => {
    try {
      await axios.put(`${API_BASE_URL}/api/v1/contact/${id}`, { status }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setSelected(null);
      fetchContacts();
    } catch (err) {
      console.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/v1/contact/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteTarget(null);
      fetchContacts();
    } catch (err) {
      console.error(err.message);
    }
  };

  const handleView = async (c) => {
    setSelected(c);
    if (!c.seen) {
      setContacts((prev) =>
        prev.map((item) => (item.id === c.id ? { ...item, seen: true } : item))
      );
      try {
        await axios.put(
          `${API_BASE_URL}/api/v1/contact/${c.id}`,
          { seen: true },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      } catch (err) {
        console.error("Failed to mark as seen:", err.message);
      }
    }
  };

  const handleOpenReply = async (c) => {
    setReplyTarget(c);
    setReplySubject(c.topic ? `Re: ${c.topic}` : "Re: SoloChat Inquiry");
    setReplyMessage("");
    setReplyAlert(null);
    if (!c.seen) {
      setContacts((prev) =>
        prev.map((item) => (item.id === c.id ? { ...item, seen: true } : item))
      );
      try {
        await axios.put(
          `${API_BASE_URL}/api/v1/contact/${c.id}`,
          { seen: true },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      } catch (err) {
        console.error("Failed to mark as seen:", err.message);
      }
    }
  };


  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) {
      setReplyAlert({ type: "error", text: "Please enter a reply message." });
      return;
    }
    setSendingReply(true);
    setReplyAlert(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/v1/contact/${replyTarget.id}/reply`,
        { subject: replySubject, message: replyMessage },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (res.status === 200) {
        setReplyAlert({ type: "success", text: "Reply sent successfully!" });
        setTimeout(() => {
          setReplyTarget(null);
          fetchContacts();
        }, 1200);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || "Failed to send reply.";
      setReplyAlert({ type: "error", text: errorMsg });
    }
    setSendingReply(false);
  };

  return (
    <div>
      <div className="inquiriesHeader">
        <h3>Inquiries</h3>
        <button className="inquiriesRefreshBtn" onClick={fetchContacts}>
          Refresh
        </button>
      </div>

      {loading && <div className="inquiriesEmpty"><p>Loading inquiries...</p></div>}

      {error && (
        <div className="inquiriesError">
          <strong>Error:</strong> {error}
          <br />
          <button onClick={fetchContacts}>Retry</button>
        </div>
      )}

      {!loading && !error && contacts.length === 0 && (
        <div className="inquiriesEmpty">
          <h4>No inquiries yet</h4>
          <p>Customer contact submissions will appear here.</p>
        </div>
      )}

      {!loading && contacts.length > 0 && (
        <div className="inquiriesTable">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Topic</th>
                <th>Message</th>
                <th>Status</th>
                <th>Seen</th>
                <th>Date</th>
                <th style={{ width: 180 }}></th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>{c.email}</td>
                  <td><span className="topicBadge">{c.topic || "-"}</span></td>
                  <td className="inquiriesBodyCell">{(c.body || "").substring(0, 60)}{c.body?.length > 60 ? "..." : ""}</td>
                  <td><span className={`statusBadge ${statusColors[c.status] || ""}`}>{c.status || "open"}</span></td>
                  <td>
                    <span className={`seenDot ${c.seen ? "seen" : "unseen"}`}
                      title={c.seen ? "Read" : "Unread"}
                      onClick={() => handleToggleSeen(c)}
                    />
                  </td>
                  <td className="inquiriesDate">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td>
                    <button className="inquiriesReplyBtn" onClick={() => handleOpenReply(c)} title="Reply by Email">
                      <ReplyFill className="me-1" />
                      Reply
                    </button>
                    <button className="inquiriesActionBtn" onClick={() => handleView(c)}>View</button>
                    <button className="inquiriesDeleteBtn" onClick={() => setDeleteTarget(c.id)}>Delete</button>

                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Inquiry Detail Modal */}
      <Modal show={!!selected} onHide={() => setSelected(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Inquiry Details</Modal.Title>
        </Modal.Header>
        {selected && (
          <Modal.Body>
            <div className="inquiryDetailRow"><strong>Email:</strong> {selected.email}</div>
            {selected.phone && <div className="inquiryDetailRow"><strong>Phone:</strong> {selected.phone}</div>}
            <div className="inquiryDetailRow"><strong>Topic:</strong> {selected.topic || "-"}</div>
            {selected.product && <div className="inquiryDetailRow"><strong>Product:</strong> {selected.product}</div>}
            <div className="inquiryDetailRow"><strong>Status:</strong> {selected.status || "open"}</div>
            <div className="inquiryDetailRow"><strong>Date:</strong> {new Date(selected.created_at).toLocaleString()}</div>
            <div className="inquiryDetailBody">
              <strong>Message:</strong>
              <p>{selected.body}</p>
            </div>
          </Modal.Body>
        )}
        <Modal.Footer style={{ justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const target = selected;
                setSelected(null);
                handleOpenReply(target);
              }}
            >
              <ReplyFill className="me-1" />
              Reply by Email
            </Button>
            {["open", "replied", "closed"].map((s) => (
              selected && selected.status !== s && (
                <Button
                  key={s}
                  variant={s === "closed" ? "danger" : s === "replied" ? "outline-primary" : "secondary"}
                  size="sm"
                  onClick={() => handleChangeStatus(selected.id, s)}
                >
                  Mark {s}
                </Button>
              )
            ))}
          </div>
          <Button variant="outline-secondary" size="sm" onClick={() => setSelected(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Reply Modal */}
      <Modal show={!!replyTarget} onHide={() => !sendingReply && setReplyTarget(null)} centered size="lg">
        <form onSubmit={handleSendReply}>
          <Modal.Header closeButton={!sendingReply}>
            <Modal.Title>
              <ReplyFill className="me-2 text-primary" />
              Reply to Inquiry
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {replyAlert && (
              <div className={replyAlert.type === "success" ? "replyAlertSuccess" : "replyAlertError"}>
                {replyAlert.text}
              </div>
            )}

            {replyTarget && (
              <>
                <div className="replyFormGroup">
                  <label>Recipient</label>
                  <input
                    type="text"
                    className="replyInput"
                    value={replyTarget.email || ""}
                    disabled
                  />
                </div>

                <div className="replyFormGroup">
                  <label>Subject</label>
                  <input
                    type="text"
                    className="replyInput"
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                    required
                  />
                </div>

                <div className="replyFormGroup">
                  <label>Original Message</label>
                  <div className="replyQuoteBox">
                    {replyTarget.body || "(No message body)"}
                  </div>
                </div>

                <div className="replyFormGroup">
                  <label>Your Reply (sent via Resend)</label>
                  <textarea
                    className="replyTextarea"
                    placeholder="Type your response to the customer here..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setReplyTarget(null)}
              disabled={sendingReply}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={sendingReply}
            >
              {sendingReply ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending via Resend...
                </>
              ) : (
                "Send Email Reply"
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Delete Inquiry</Modal.Title></Modal.Header>
        <Modal.Body>Are you sure you want to delete this inquiry?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => handleDelete(deleteTarget)}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Inquiries;

